import { useEffect, useRef } from "react"
import { setAuthHeader } from "@/api/service"
import { storeTgUserId } from "@/helpers/tgSession"
import { getTgInitData, getTgUserId } from "@/helpers/tgWebApp"
import { useHasSession } from "@/hooks/useHasSession"
import { useAppDispatch, useAppSelector } from "@/store"
import { useLoginWithTelegramMiniAppMutation } from "@/store/apis/authAPI"
import { AppSelectors, setReAuthState } from "@/store/slices/app"
import { setIsAuthorized } from "@/store/slices/player"
import { classifyAutoAuthFailure, decideAutoAuth } from "./helpers"

export const useAutoAuth = () => {
  const dispatch = useAppDispatch()
  const isTMA = useAppSelector(AppSelectors.selectIsTMA())
  const reAuthState = useAppSelector(AppSelectors.selectReAuthState())
  const hasSession = useHasSession()
  const [loginWithTelegramMiniApp] = useLoginWithTelegramMiniAppMutation()
  const isRequestInFlight = useRef(false)

  useEffect(() => {
    const decision = decideAutoAuth({
      isTMA,
      hasSession,
      reAuthKind: reAuthState.kind,
      initData: getTgInitData()
    })

    if (decision.action === "none") {
      return
    }

    if (decision.action === "fail") {
      dispatch(setReAuthState({ kind: "error", cause: decision.cause }))
      return
    }

    if (isRequestInFlight.current) {
      return
    }

    isRequestInFlight.current = true
    dispatch(setReAuthState({ kind: "loading" }))

    const signIn = async () => {
      try {
        const tokens = await loginWithTelegramMiniApp({
          initData: decision.initData
        }).unwrap()

        setAuthHeader(tokens.accessToken, tokens.refreshToken)

        const launchTgUserId = getTgUserId()
        if (launchTgUserId !== undefined) {
          storeTgUserId(launchTgUserId)
        }

        dispatch(setIsAuthorized(true))
        dispatch(setReAuthState({ kind: "idle" }))
      } catch (error) {
        dispatch(
          setReAuthState({
            kind: "error",
            cause: classifyAutoAuthFailure(
              (error as { status?: number }).status
            )
          })
        )
      } finally {
        isRequestInFlight.current = false
      }
    }

    void signIn()
  }, [isTMA, hasSession, reAuthState.kind])
}
